// src/components/CloseEnvivoModal.jsx
import React, { useState } from 'react';
import { X, CreditCard, DollarSign, Calculator, Plus, Trash2 } from 'lucide-react';
import apiService from '../services/apiService';

const CloseEnvivoModal = ({ envivoId, envivoName, onClose, onSuccess }) => {
  const [includePayments, setIncludePayments] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Agregar nuevo pago
  const addPayment = () => {
    setPayments([...payments, {
      cardName: '',
      amountUsd: '',
      amountCop: '',
      id: Date.now() // ID temporal para el key
    }]);
  };

  // Eliminar pago
  const removePayment = (id) => {
    setPayments(payments.filter(p => p.id !== id));
  };

  // Actualizar pago
  const updatePayment = (id, field, value) => {
    setPayments(payments.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  // Calcular TRM automáticamente
  const calculateTRM = (usd, cop) => {
    if (usd && cop && parseFloat(usd) > 0) {
      return (parseFloat(cop) / parseFloat(usd)).toFixed(2);
    }
    return '';
  };

  // Validar formulario
  const validateForm = () => {
    if (includePayments) {
      for (let payment of payments) {
        if (!payment.cardName.trim()) {
          setError('El nombre de la tarjeta es obligatorio');
          return false;
        }
        if (!payment.amountUsd && !payment.amountCop) {
          setError('Debe ingresar al menos un monto (USD o COP)');
          return false;
        }
        if (payment.amountUsd && parseFloat(payment.amountUsd) <= 0) {
          setError('El monto en USD debe ser mayor a 0');
          return false;
        }
        if (payment.amountCop && parseFloat(payment.amountCop) <= 0) {
          setError('El monto en COP debe ser mayor a 0');
          return false;
        }
      }
    }
    return true;
  };

  // Cerrar en vivo
  const handleCloseEnvivo = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const body = {
        status: 'closed',
        endTime: new Date().toISOString()
      };

      // Agregar pagos si se incluyen
      if (includePayments && payments.length > 0) {
        body.payments = payments.map(payment => ({
          cardName: payment.cardName,
          amountUsd: payment.amountUsd ? parseFloat(payment.amountUsd) : null,
          amountCop: payment.amountCop ? parseFloat(payment.amountCop) : null
        }));
      }

      await apiService.envivo.update(envivoId, body);
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError('Error al cerrar en vivo: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <X className="text-red-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Cerrar En Vivo</h3>
              <p className="text-sm text-gray-600">{envivoName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Opción de Pagos */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <input
              type="checkbox"
              id="includePayments"
              checked={includePayments}
              onChange={(e) => setIncludePayments(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="includePayments" className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <CreditCard size={16} className="text-blue-600" />
              Registrar pagos de tarjetas utilizadas
            </label>
          </div>
          
          <p className="text-xs text-gray-500 ml-7">
            Opcional: Registra las tarjetas de crédito utilizadas y los montos pagados
          </p>
        </div>

        {/* Formulario de Pagos */}
        {includePayments && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-800 flex items-center gap-2">
                <DollarSign size={16} className="text-green-600" />
                Pagos con Tarjetas
              </h4>
              <button
                onClick={addPayment}
                className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus size={14} />
                Agregar Tarjeta
              </button>
            </div>

            {payments.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <CreditCard size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500">No hay pagos registrados</p>
                <p className="text-xs text-gray-400">Haz clic en "Agregar Tarjeta" para comenzar</p>
              </div>
            ) : (
              <div className="space-y-4">
                {payments.map((payment) => (
                  <div key={payment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-gray-700">Tarjeta #{payments.indexOf(payment) + 1}</h5>
                      <button
                        onClick={() => removePayment(payment.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Nombre de la tarjeta */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nombre de la Tarjeta *
                        </label>
                        <input
                          type="text"
                          value={payment.cardName}
                          onChange={(e) => updatePayment(payment.id, 'cardName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ej: Visa, Mastercard"
                          required
                        />
                      </div>

                      {/* Monto en USD */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Monto en USD
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={payment.amountUsd}
                            onChange={(e) => updatePayment(payment.id, 'amountUsd', e.target.value)}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>

                      {/* Monto en COP */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Monto en COP
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={payment.amountCop}
                            onChange={(e) => updatePayment(payment.id, 'amountCop', e.target.value)}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="0"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>

                    {/* TRM Calculada */}
                    {(payment.amountUsd || payment.amountCop) && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <div className="flex items-center gap-2 text-sm">
                          <Calculator size={14} className="text-gray-500" />
                          <span className="text-gray-600">TRM Calculada:</span>
                          <span className="font-medium text-gray-800">
                            {calculateTRM(payment.amountUsd, payment.amountCop)} COP por USD
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Resumen */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-800 mb-2">Resumen del Cierre</h4>
          <div className="space-y-1 text-sm text-gray-600">
            <p>• En Vivo: <span className="font-medium">{envivoName}</span></p>
            <p>• Fecha de cierre: <span className="font-medium">{new Date().toLocaleString('es-CO')}</span></p>
            <p>• Pagos registrados: <span className="font-medium">{includePayments ? payments.length : 0}</span></p>
            {includePayments && payments.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="font-medium text-gray-800">Total en tarjetas:</p>
                <div className="ml-4 space-y-1">
                  {payments.map((payment, index) => (
                    <p key={payment.id} className="text-xs">
                      {payment.cardName}: 
                      {payment.amountUsd && ` $${parseFloat(payment.amountUsd).toFixed(2)} USD`}
                      {payment.amountUsd && payment.amountCop && ' + '}
                      {payment.amountCop && ` $${parseFloat(payment.amountCop).toLocaleString('es-CO')} COP`}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleCloseEnvivo}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Cerrando...
              </>
            ) : (
              <>
                <X size={16} />
                Cerrar En Vivo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloseEnvivoModal;
